import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { DayPicker, type DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  calendarClassNames,
  datePickerTriggerRecipe,
} from '../recipes/calendar.recipe';
import { popoverRecipe } from '../recipes/overlay.recipe';
import { Button } from './Button';
import { useFieldControlProps } from './field-context';

export type { DateRange };

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;

export interface PopoverContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {
  padding?: 'none' | 'sm' | 'md';
}

/** A floating panel anchored to a trigger. No backdrop, unlike a Dialog. */
export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(function PopoverContent(
  { className, padding, align = 'start', sideOffset = 4, ...props },
  ref
) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(popoverRecipe({ padding }), className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});

/* DayPicker's props are a discriminated union on `mode`, which an interface
 * cannot extend — hence a type alias. */
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * The calendar grid on its own, for cases that need it inline rather than in
 * a popover — a date range filter panel, for instance.
 *
 * Every visual decision lives in `calendarClassNames`, so a designer can
 * restyle it without learning react-day-picker's internals.
 */
export function Calendar({ className, classNames, ...props }: CalendarProps) {
  return (
    <DayPicker
      className={cn('p-3', className)}
      classNames={{ ...calendarClassNames, ...classNames }}
      components={{
        IconLeft: () => <ChevronLeft className="size-icon-sm" />,
        IconRight: () => <ChevronRight className="size-icon-sm" />,
      }}
      {...props}
    />
  );
}

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  /** date-fns format string for the button label. */
  displayFormat?: string;
  disabled?: boolean;
  id?: string;
  /** Restrict selectable days, e.g. `{ before: new Date() }`. */
  disabledDays?: React.ComponentProps<typeof DayPicker>['disabled'];
  className?: string;
}

/**
 * Single-date picker.
 *
 * Like the other controls, it picks up its id and disabled/invalid state from
 * a surrounding Field, so the usual case is:
 *
 *   <Field label="Release date" error={errors.date?.message}>
 *     <DatePicker value={date} onChange={setDate} />
 *   </Field>
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  displayFormat = 'PP',
  disabled,
  id,
  disabledDays,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const fieldProps = useFieldControlProps({ id, disabled });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={fieldProps.id}
          type="button"
          variant="outline"
          fullWidth
          disabled={fieldProps.disabled}
          aria-invalid={fieldProps['aria-invalid']}
          aria-describedby={fieldProps['aria-describedby']}
          leadingIcon={<CalendarIcon />}
          className={cn(
            datePickerTriggerRecipe({ hasValue: Boolean(value) }),
            className
          )}
        >
          {value ? format(value, displayFormat) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent padding="none">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(date) => {
            onChange?.(date);
            // Close on pick: for a single date there is nothing left to do.
            setOpen(false);
          }}
          disabled={disabledDays}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export interface DateRangePickerProps
  extends Omit<DatePickerProps, 'value' | 'onChange'> {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
}

